// 🔍 Sentry Error Tracking - initialize based on consent
import * as Sentry from "@sentry/react";
import { Analytics } from "@vercel/analytics/react";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import App from "./App";
import GlobalErrorBoundary from "./components/GlobalErrorBoundary";
import { ScrollToTop } from "./components/ScrollToTop";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { UserPreferencesProvider } from "./contexts/UserPreferencesContext";

// Check cookie consent before initializing tracking services
const getStoredConsent = () => {
  try {
    const stored = localStorage.getItem("cookie_consent");
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed.settings;
  } catch {
    return null;
  }
};

const consent = getStoredConsent();

// Require EXPLICIT consent - no tracking on first visit
// User must click "Accept" before any tracking starts
const hasPerformanceConsent = consent?.performance === true;
const hasAnalyticsConsent = consent?.analytics === true;

// Only initialize Sentry in PRODUCTION - never on localhost
// This ensures no localhost:3000 traffic appears in Sentry
const isProduction = import.meta.env.PROD;
const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

const shouldInitSentry = hasPerformanceConsent && isProduction && !isLocalhost;

if (shouldInitSentry) {
  Sentry.init({
    dsn:
      import.meta.env.VITE_SENTRY_DSN ||
      "https://29033733efaae3bd1816fe769b309739@o4510533969838080.ingest.de.sentry.io/4510533978423376",
    environment: import.meta.env.MODE,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.browserProfilingIntegration(),
    ],
    tracesSampleRate: 1.0,
    // Only trace requests to the production domain
    tracePropagationTargets: [/^https:\/\/streameater\.vercel\.app/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    profilesSampleRate: 1.0,
    _experiments: {
      enableLogs: true,
    },
    enabled: true,
    // Extra safety: filter out any localhost events that might slip through
    beforeSend(event) {
      const url = event.request?.url || window.location.href;
      if (url.includes("localhost") || url.includes("127.0.0.1")) {
        return null; // Don't send localhost events
      }
      return event;
    },
    beforeSendTransaction(event) {
      const url = event.request?.url || window.location.href;
      if (url.includes("localhost") || url.includes("127.0.0.1")) {
        return null; // Don't send localhost transactions
      }
      return event;
    },
  });
}

// Register Service Worker for caching
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        console.log("[SW] Registered:", reg.scope);
      })
      .catch((err) => {
        console.warn("[SW] Registration failed:", err);
      });
  });
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <GlobalErrorBoundary>
      <AuthProvider>
        <LanguageProvider>
          <UserPreferencesProvider>
            <BrowserRouter>
              <ScrollToTop />
              <App />
            </BrowserRouter>
          </UserPreferencesProvider>
          {/* Only render Analytics if user EXPLICITLY consented */}
          {hasAnalyticsConsent && <Analytics debug={false} />}
        </LanguageProvider>
      </AuthProvider>
    </GlobalErrorBoundary>
  </React.StrictMode>,
);
